import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getNormaleDateOrMonth, intlFormatDateShort, intlFormatDateWithHour } from "../../../../utils/time-utils";
import ExportToExcelDialog from "../../ExportToExcelDialog/ExportToExcelDialog";
import { excelStyles } from "../../utils";
import { exportTnAToExcel } from "../../../../utils/export-presence-and-leaves";
import { LoadResponse } from "../../../../models/api/load";
import { TnAAbsenceDTO } from "../../../../models/tna";
import { AbsencesPeriodQuickFilter } from "../AbsencesPeriodQuickfilter/interfaces";
import { State } from "../AllEmployeeLeaves";

interface Props {
  disabled: boolean;
  absencesPeriodQuickFilter: AbsencesPeriodQuickFilter;
  state: State;
  loadAllAbsences: (signal?: AbortSignal) => Promise<LoadResponse<TnAAbsenceDTO> | undefined>;
}

const ExportAllEmployeeLeavesToExcel = ({ disabled, absencesPeriodQuickFilter, state, loadAllAbsences }: Props) => {
  const { t } = useTranslation();
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";
  const tRPLeaves = "components.my_leaves";
  const translationFields = `${tRPLeaves}.fields`;

  const rowsBeforeHeaderInExcel = useCallback(() => {
    const fromDate = !!absencesPeriodQuickFilter.absencesPeriod.fromDate
      ? intlFormatDateShort(absencesPeriodQuickFilter.absencesPeriod.fromDate)
      : null;
    const toDate = !!absencesPeriodQuickFilter.absencesPeriod.toDate
      ? intlFormatDateShort(absencesPeriodQuickFilter.absencesPeriod.toDate)
      : null;
    const period =
      fromDate && toDate
        ? `${fromDate} - ${toDate}`
        : !!fromDate
        ? `From ${fromDate}`
        : !!toDate
        ? `To ${toDate}`
        : "All";

    return [
      {
        data: [`Employee leaves`],
        fontSize: 16,
        isBold: true,
      },
      {
        data: [`Report date ${intlFormatDateWithHour(new Date())}`],
        fontSize: 14,
        isBold: false,
        emptyRowsUnder: 1,
      },
      {
        data: [`Period: ${period}`],
        fontSize: 12,
        isBold: false,
        emptyRowsUnder: 1,
      },
      {
        data: [
          `Employee: ${
            state.employee?.id ? state.employee?.name : !state.employee?.id && state.allEmployees ? "All" : ""
          }`,
        ],
        fontSize: 12,
        isBold: false,
      },
    ];
  }, [absencesPeriodQuickFilter.absencesPeriod, state.employee?.id, state.allEmployees]);

  const headerNames = useMemo(() => {
    return [
      `${t(`${tRPCommon}.employee_code`)}`,
      `${t(`${tRPCommon}.internal_id`)}`,
      `${t(`common_words.employee`)}`,
      `${t(`${tRPCommon}.birth_date`)}`,
      `${t(`common_words.legal_entity`)}`,
      `${t(`common_words.tna_employee_group`)}`,
      `${t(`${translationFields}.absence_type`)}`,
      `${t(`common_words.comment`)}`,
      `${t(`${translationFields}.from_date`)}`,
      `${t(`${translationFields}.to_date`)}`,
      `${t(`${translationFields}.is_approved_rejected`)}`,
      `${t(`${translationFields}.accepted_rejected_by_client`)}`,
    ];
  }, []);

  const dataToExportAsExcel = useCallback(
    (items?: TnAAbsenceDTO[]) => {
      return (items ?? state.items).map((item) => {
        return {
          [`${t(`${tRPCommon}.employee_code`)}`]: { value: item.employee?.employeeCode ?? " " },
          [`${t(`${tRPCommon}.internal_id`)}`]: { value: item.employee?.internalId ?? " " },
          [`${t(`common_words.employee`)}`]: {
            value: item.employee?.name ?? " ",
            style: { ...excelStyles.employeeCellStyle },
          },
          [`${t(`${tRPCommon}.birth_date`)}`]: {
            value: item.employee?.birthDate ? intlFormatDateShort(item.employee?.birthDate) : " ",
          },
          [`${t(`common_words.legal_entity`)}`]: { value: item.employee?.legalEntity?.name ?? " " },
          [`${t(`common_words.tna_employee_group`)}`]: { value: item?.employee?.tnAGroup?.name ?? " " },
          [`${t(`${translationFields}.absence_type`)}`]: {
            value: !!item?.absenceType?.name ? `${item?.absenceType?.name} (${item?.countedDurationDays} day(s))` : " ",
          },
          [`${t(`common_words.comment`)}`]: { value: !!item?.comment ? `${item?.comment}` : " " },
          [`${t(`${translationFields}.from_date`)}`]: {
            value: !!item?.fromDate
              ? `${intlFormatDateShort(new Date(item.fromDate.year, item.fromDate.month - 1, item.fromDate.day))}`
              : " ",
          },
          [`${t(`${translationFields}.to_date`)}`]: {
            value: !!item?.toDate
              ? `${intlFormatDateShort(new Date(item.toDate.year, item.toDate.month - 1, item.toDate.day))}`
              : " ",
          },
          [`${t(`${translationFields}.is_approved_rejected`)}`]: {
            value:
              item?.isApproved && !item?.isCancelled
                ? `Approved by ${item?.modifiedByUser?.name}`
                : item?.isRejected && !item?.isCancelled
                ? `Rejected by ${item?.modifiedByUser?.name}`
                : " ",
          },
          [`${t(`${translationFields}.accepted_rejected_by_client`)}`]: {
            value: !!item?.acceptedRejectedByUser?.name ? `${item.acceptedRejectedByUser.name}` : " ",
          },
          sortDirection: headerNames,
        };
      });
    },
    [headerNames, state.items]
  );

  const exportToExcel = useCallback(
    async (option: "exportAllData" | "exportCurrent" = "exportCurrent") => {
      if (option === "exportCurrent") {
        exportTnAToExcel(
          dataToExportAsExcel(),
          headerNames as string[],
          `${new Date().getFullYear()} ${getNormaleDateOrMonth(new Date().getMonth() + 1)} ${getNormaleDateOrMonth(
            new Date().getDate()
          )} All employee leaves${state.employee?.id && state.employee?.name ? ` for ${state.employee?.name}` : ""}`,
          rowsBeforeHeaderInExcel()
        );
      }

      if (option === "exportAllData") {
        const allAbsences = await loadAllAbsences();

        if (!!allAbsences?.error || !allAbsences?.items) return;

        exportTnAToExcel(
          dataToExportAsExcel(allAbsences?.items),
          headerNames as string[],
          `${new Date().getFullYear()} ${getNormaleDateOrMonth(new Date().getMonth() + 1)} ${getNormaleDateOrMonth(
            new Date().getDate()
          )} All employee leaves${state.employee?.id && state.employee?.name ? ` for ${state.employee?.name}` : ""}`,
          rowsBeforeHeaderInExcel()
        );
      }
    },
    [rowsBeforeHeaderInExcel, headerNames, dataToExportAsExcel, loadAllAbsences, state.employee]
  );

  return (
    <ExportToExcelDialog
      handleExportToExcel={exportToExcel}
      disabled={disabled}
    />
  );
};

export default ExportAllEmployeeLeavesToExcel;
